import { useState } from "react";
import { Tag, Loader2, CheckCircle2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { useLocale } from "@/contexts/LocaleContext";

export function PromoCodeInput() {
  const { t } = useLocale();
  const { user, refreshProfile } = useAuth();
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [redeemed, setRedeemed] = useState(false);

  const redeem = async () => {
    if (!code.trim() || !user) return;
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error("Not authenticated");

      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/redeem-promo`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ code: code.trim() }),
        }
      );

      const json = await res.json();
      if (!res.ok) throw new Error(json.error || "Redemption failed");

      setRedeemed(true);
      toast({ title: "✅ Promo code applied!", description: json.message });
      await refreshProfile();
      setTimeout(() => { setRedeemed(false); setCode(""); }, 3000);
    } catch (err: any) {
      toast({ title: "Failed", description: err.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <div className="glass-card p-4">
      <div className="flex items-center gap-2 mb-3">
        <Tag className="w-4 h-4 text-primary" />
        <span className="text-sm font-semibold text-foreground">Promo Code</span>
      </div>
      <div className="flex gap-2">
        <input
          value={code}
          onChange={e => setCode(e.target.value.toUpperCase())}
          placeholder="Enter promo code..."
          className="flex-1 bg-background border border-border/50 rounded-lg px-3 py-2 text-sm font-mono text-foreground placeholder:text-muted-foreground"
          disabled={loading || redeemed}
          onKeyDown={e => e.key === "Enter" && redeem()}
        />
        <button onClick={redeem} disabled={loading || redeemed || !code.trim()}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50 flex items-center gap-2">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : redeemed ? <CheckCircle2 className="w-4 h-4" /> : null}
          {redeemed ? "Applied!" : "Redeem"}
        </button>
      </div>
    </div>
  );
}
